/**
 * Capability derivation + the capability vocabulary the SPA gates on.
 *
 * TWO sources of truth, in priority order:
 *
 *  1. The SERVER-resolved capability list, fetched from Oxbo-M
 *     `GET /users/me/capabilities` (see mApi.getMyCapabilities). This is the
 *     real, fine-grained set — resolved from role_assignment server-side. When
 *     it is available, `useCapabilities().can()` reads it.
 *  2. The legacy role→caps derivation below (`catalogCapabilities`). It is the
 *     FALLBACK used while the server list is still loading OR if the fetch
 *     errored — so the SPA never regresses below todays behaviour (the server
 *     is the real gate regardless; this UI layer is belt-and-suspenders, §22c).
 *
 * `X-Oxbo-Caps` (forwarded by mApi) remains a client HINT only; Oxbo-M never
 * reads it as a gate — it re-resolves from role_assignment on every call.
 *
 * Wildcard semantics: `catalog:*:write` is the stored/granted catalog-manage
 * key; the semantic gate key is `catalog:manage`, which the wildcard satisfies
 * via the `catalog:` prefix rule. This matches the Oxbo-M resolver byte-for-byte
 * (oxbo-masters security/capabilities.caps_satisfy).
 */
import type { UserRole } from "@app/slices/prefsSlice";

/** The wildcard a catalog-admin holds. Covers `catalog:manage` + `catalog:{k}:write`. */
export const CATALOG_WILDCARD_CAP = "catalog:*:write";

/**
 * The finite capability vocabulary (mirrors Oxbo-M CAPABILITY_VOCAB). The SPA
 * gates features on these SEMANTIC keys; the catalog one is `catalog:manage`
 * (satisfied by the stored `catalog:*:write` wildcard).
 */
export const CAPABILITIES = {
  wellCreate: "well:create",
  eventCreate: "event:create",
  importRun: "import:run",
  catalogManage: "catalog:manage",
  lineageView: "lineage:view",
  reportGenerate: "report:generate",
  afeManage: "afe:manage",
  aclManage: "acl:manage",
} as const;

export type CapabilityKey = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

/**
 * Legacy role→caps derivation — the FALLBACK set used before the server list
 * arrives (or if it errored). A `data_admin` holds the full admin surface
 * (exactly todays behaviour); a `field` user holds none. Kept so the SPA
 * never flashes features hidden nor hides what the role used to show.
 */
export function roleCapabilities(role: UserRole): string[] {
  return role === "data_admin"
    ? [
        CATALOG_WILDCARD_CAP,
        CAPABILITIES.wellCreate,
        CAPABILITIES.eventCreate,
        CAPABILITIES.importRun,
        CAPABILITIES.lineageView,
        CAPABILITIES.reportGenerate,
        CAPABILITIES.afeManage,
        // NB: acl:manage is intentionally NOT in the role fallback — it is a
        // per-user-only super-capability (never conferred by a role template),
        // so the ACL screen appears only once the server list confirms it.
      ]
    : [];
}

/**
 * Back-compat alias: the catalog-only caps the role holds. mApi still forwards
 * these as the `X-Oxbo-Caps` hint. A `data_admin` → the wildcard; `field` → none.
 */
export function catalogCapabilities(role: UserRole): string[] {
  return role === "data_admin" ? [CATALOG_WILDCARD_CAP] : [];
}

/**
 * Does `caps` satisfy the required `capability`? Supports the `catalog:*:write`
 * wildcard (holder may manage any catalog) as well as exact grants. Identical
 * to the Oxbo-M server resolver (caps_satisfy), so client + server agree.
 */
export function capsSatisfy(caps: string[], capability: string): boolean {
  if (caps.includes(capability)) return true;
  if (caps.includes(CATALOG_WILDCARD_CAP) && capability.startsWith("catalog:")) return true;
  return false;
}
